<script>
import * as PIXI from 'pixi.js';
import { mapGetters } from 'vuex';
import utils from '@/utils';
import TweenMax, { Linear, Sine, Expo } from 'gsap/TweenMax';

export default {
  name: 'PixiHeat',
  data() {
    return {
      app: null,

      //interval
      tipsInterval: null,
      refonteInterval: null,
      meltInterval: null,
      
      //containers
      brush: null,
      bgContainer: null,
      whiteFill: null,
      renderTexture: null,

      //melt
      meltProps: {
        amount: 1,
        timer: 5000,
        round: 0,
        volume: 3,
        x: [.7, .1, .3, .2,  .5, .75,  .8, .5,  .1, .15, .9,  .85, .25, .4,   1,  .5,  0,  .30],
        y: [.8, .15, .6, .95, .9, .45, .1, .25, .5, .35, .25, .65, 0,   .15,  .5, .5,  .8, .78],
        n: [1, 2, 3, 1, 2, 3, 1, 2, 3, 1, 2, 3, 1, 2, 3, 1, 2, 3]
      },

      //flags
      isDrawing: false,
      hasMoved: false,

      collisions: 0,
    }
  },
  computed: {
    ...mapGetters({
      viewport: 'App/viewport',
      isTablet: 'App/isTablet',
      showTips: 'Interface/showTips',
    }),
    stage() {
      return this.app.stage;
    },
    headerHeight() {
      return 46;
    },
    meltShapes() {
      return [
        this.$store.getters['Textures/byId']('melt1TB4').texture,
        this.$store.getters['Textures/byId']('melt2TB4').texture,
        this.$store.getters['Textures/byId']('melt3TB4').texture,
      ]
    },
  },
  mounted() {
    if(this.app) return;

    this.app = new PIXI.Application({
      width: this.viewport.width, height: this.viewport.height - this.headerHeight,
      backgroundColor: 0x000000,
      resolution: 1,
      view: document.querySelector('.pixi-stage'),
      transparent: true,
    });

    this.$nextTick( () => {
      this.init();
      this.resize(this.viewport.width, this.viewport.height);
    });
  },
  beforeDestroy() {
    clearInterval(this.meltInterval);
    this.stopTipsTimer();
    this.stopRefonteInterval();

    if(this.stage) {
      this.stage.removeChildren();
      this.stage.off();
    }

    if(this.app) {
      this.app.destroy();
      this.app = null;
    }
  },
  methods: {
    init() {
      this.bgContainer = new PIXI.Sprite();
      this.stage.addChild(this.bgContainer);

      //background
      const bgBefore = new PIXI.Sprite(this.$store.getters['Textures/byId']('heat-bg-before').texture);
      bgBefore.name = "bgBefore";
      this.bgContainer.addChild(bgBefore);

      const bgAfter = new PIXI.Sprite(this.$store.getters['Textures/byId']('heat-bg-after').texture);
      bgAfter.name = "bgAfter";
      this.bgContainer.addChild(bgAfter);

      //---- render texure
      this.renderTexture = PIXI.RenderTexture.create(this.viewport.width, this.viewport.height);

      const rdSprite = new PIXI.Sprite(this.renderTexture);
      rdSprite.name = "rdSprite";
      this.stage.addChild(rdSprite);

      //white fill
      this.whiteFill = new PIXI.Graphics();
      this.whiteFill.beginFill(0xFFFFFF);
      this.whiteFill.drawRect(0, 0, window.innerWidth, window.innerHeight);
      this.whiteFill.endFill();
      this.app.renderer.render(this.whiteFill, this.renderTexture, false, null, false);

      this.initMelts();

      //animation
      TweenMax.to([bgBefore, bgAfter], 2, {y: +((this.viewport.height - 200) * 0.2), ease:Sine.easeInOut, repeat: -1, repeatDelay: 0.1, yoyo: true, delay:.3});
     
      TweenMax.from(bgAfter, 2.5, {overwrite:false, alpha:0, ease:Linear.easeNone, delay:4});
    },

    initMelts() {
      const meltContainer = new PIXI.Sprite();
      meltContainer.name = "meltContainer";
      meltContainer.mask = this.stage.getChildByName("rdSprite");
      this.stage.addChild(meltContainer);

      TweenMax.delayedCall(9, this.startDrawing);
      this.meltInterval = setTimeout(this.addMelt.bind(this), 4000 + this.meltProps.timer);
    },

    startDrawing() {
      
      //brush
      this.brush = PIXI.Sprite.from('/assets/tableaux/heat/brush.png');
      this.brush.anchor.set(this.isTablet ? .75 : .5);

      this.stage.interactive = true;
      this.stage.on('pointerdown', this.down);
      this.stage.on('pointerup', this.up);
      this.stage.on('pointermove', this.move);

      this.stopTipsTimer();
      this.startTipsTimer();
    },

    stopDrawing() {
      clearInterval(this.meltInterval);
      this.stopTipsTimer();
      this.stopRefonteInterval();

      this.stage.interactive = false;
      this.stage.off();

      this.$emit('leave');

      TweenMax.killTweensOf([this.bgContainer.getChildByName('bgBefore'), this.bgContainer.getChildByName('bgAfter')]);
      TweenMax.to([this.bgContainer.getChildByName('bgBefore'), this.bgContainer.getChildByName('bgAfter')], 2, {y: this.viewport.height * .5, ease:Sine.easeInOut});
    },

    startTipsTimer() {
      this.tipsInterval = setInterval(() => {
        this.$store.dispatch('Interface/SHOW_TIPS');
      }, 6500);
    },

    stopTipsTimer() {
      clearInterval(this.tipsInterval);
      if(this.showTips) this.$store.dispatch('Interface/HIDE_TIPS');
    },

    startRefonteInterval() {
      this.refonteInterval = setTimeout(() => {
        this.hasMoved = false;
      }, utils.randomBetween(400, 800));
    },

    stopRefonteInterval() {
      clearTimeout(this.refonteInterval);
    },

    addMelt() {

      if(!this.meltProps.x.length) {
        this.stopDrawing();
        return;
      }

      this.app.renderer.render(this.whiteFill, this.renderTexture, false, null, false);
      this.meltProps.volume += 0.5;

      // get position
      for(var i=0; i < this.meltProps.amount; i++) {
       
          const meltContainer = this.stage.getChildByName("meltContainer");
          const props = this.getMeltProps();
          const texture = this.meltShapes[props.textureIndex];
          const melt = new PIXI.Sprite(texture);
          melt.anchor.set(.5);
          melt.scale.set(props.scale);
          melt.x = this.viewport.width * props.x;
          melt.y = this.viewport.height * props.y;
          meltContainer.addChild(melt);

          TweenMax.from(melt.scale, 2, {x:0, y:0, ease:Expo.easeOut, delay:i*.1});
          TweenMax.from(melt, .3, {alpha:0, ease:Expo.easeOut, delay:i*.1});

          //son
          const meltSound = "fonte" + (Math.round(Math.random()+1));
          this.$store.dispatch('Sound/PLAY', {id:meltSound, volume: Math.min(this.meltProps.volume, 6)});
      }

      this.meltProps.amount += 1;
      this.meltProps.timer -= utils.randomBetween(1100, 1250);
      this.meltProps.round++;

      this.meltInterval = setTimeout(this.addMelt.bind(this), 4000 + this.meltProps.timer);
    },

    getMeltProps() {
      const random = utils.randomBetween(0, this.meltProps.x.length - 1);

      const props =  {
        index: random,
        x: this.meltProps.x[random],
        y: this.meltProps.y[random],
        textureIndex: this.meltProps.n[random] - 1,
        scale: this.isTablet ? .45 : .25
      };

      this.meltProps.x.splice(random, 1);
      this.meltProps.y.splice(random, 1);
      return props;
    },

    down(event) {
      this.isDrawing = true;
      this.move(event)
      this.stopTipsTimer();
    },

    up() {
      this.isDrawing = false;
      this.stopTipsTimer();
      this.startTipsTimer();
    },

    move(event) {
      if(!this.isDrawing) return;
      
      this.brush.position.copyFrom(event.data.global);
      this.app.renderer.render(this.brush, this.renderTexture, false, null, false);

      //son doigt
      if(!this.hasMoved){
        this.hasMoved = true;
        
        this.hasMoved = true;
        this.$store.dispatch('Sound/PLAY', {id: Math.random() > .5 ? 'refonte1' : 'refonte1', rate:Math.random()*0.8+0.7})

        this.startRefonteInterval();
      }
    },

    resize(w, h) {
      this.app.renderer.resize(w, h - this.headerHeight);

      const bgContainerIds = [{id:'bgBefore', keepRatio:true, toTop:false}, {id:'bgAfter', keepRatio:true, toTop:false}];
      bgContainerIds.forEach( asset => {
        utils.toFullscreen(this.bgContainer.getChildByName(asset.id), {w, h, keepRatio:asset.keepRatio, toTop:asset.toTop});
      });
    },
  }
}
</script>

<template>
  <canvas ref='Canvas' :width="viewport.width" :height="viewport.height - headerHeight" class='pixi-stage' />
</template>

<style scoped lang="scss">
.pixi-stage {
  width: 100%;
  height: 100%;
  position: relative;
  top: 0;
  left: 0px;
}
</style>
